import React from "react";
import { Input } from "@src/lib/ui/Input";
import { Switch } from "@src/lib/ui/Switch";
import Text from "@src/lib/ui/Text";
import { DictationTextarea } from "@src/features/aiAssist/DictationButton";
import { ScheduleDraft, WEEKDAYS } from "./agentSchedule";

type Props = {
  value: ScheduleDraft;
  tzid: string | null;
  runsAsViewer: boolean;
  customRule: string | null;
  onChange: (next: ScheduleDraft) => void;
  onUseSimple: () => void;
};

const selectClass =
  "h-9 rounded-lg border border-border-secondary bg-bg-primary px-3 font-sans-v4 text-s-v4 text-text-primary";

const AgentScheduleFields: React.FC<Props> = ({
  value,
  tzid,
  runsAsViewer,
  customRule,
  onChange,
  onUseSimple,
}) => (
  <div className="flex flex-col gap-3" data-testid="agent-schedule">
    <div className="flex items-center justify-between gap-3">
      <label htmlFor="agent-schedule-enabled">
        <Text variant="compact-title">Run on a schedule</Text>
      </label>
      <Switch
        id="agent-schedule-enabled"
        checked={value.enabled}
        onCheckedChange={(enabled) => onChange({ ...value, enabled })}
        data-testid="agent-schedule-enabled"
      />
    </div>
    {value.enabled && (
      <>
        <Text variant="compact-description" color="muted" multiline>
          At most once a day
          {tzid ? ` (${tzid.replace(/_/g, " ")})` : " (your org's timezone)"}.
          The run acts as you and sends the prompt below into your chat with
          this agent.
          {!runsAsViewer && " Saving takes over a schedule another admin set."}
        </Text>
        {customRule ? (
          <div className="flex flex-col gap-2">
            <Text
              variant="compact-description"
              className="font-mono"
              data-testid="agent-schedule-custom"
            >
              {customRule}
            </Text>
            <button
              type="button"
              className="self-start text-s-v4 text-text-secondary underline"
              onClick={onUseSimple}
              data-testid="agent-schedule-use-simple"
            >
              Use a simple schedule
            </button>
          </div>
        ) : (
          <div className="flex flex-wrap gap-2">
            <select
              className={selectClass}
              value={value.cadence}
              aria-label="How often"
              onChange={(event) =>
                onChange({
                  ...value,
                  cadence: event.target.value as ScheduleDraft["cadence"],
                })
              }
              data-testid="agent-schedule-cadence"
            >
              <option value="daily">Every day</option>
              <option value="weekdays">Weekdays</option>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
            </select>
            {value.cadence === "weekly" && (
              <select
                className={selectClass}
                value={value.weekday}
                aria-label="Day of week"
                onChange={(event) =>
                  onChange({
                    ...value,
                    weekday: event.target.value as ScheduleDraft["weekday"],
                  })
                }
                data-testid="agent-schedule-weekday"
              >
                {WEEKDAYS.map((day) => (
                  <option key={day.value} value={day.value}>
                    {day.label}
                  </option>
                ))}
              </select>
            )}
            {value.cadence === "monthly" && (
              <Input
                type="number"
                min={1}
                max={28}
                label="Day of month"
                value={String(value.monthday)}
                onChange={(event) =>
                  onChange({
                    ...value,
                    monthday: Number(event.target.value) || 1,
                  })
                }
                data-testid="agent-schedule-monthday"
              />
            )}
            <Input
              type="time"
              label="Time"
              value={value.time}
              onChange={(event) =>
                onChange({ ...value, time: event.target.value })
              }
              data-testid="agent-schedule-time"
            />
          </div>
        )}
        <div className="flex flex-col gap-1.5">
          <label htmlFor="agent-schedule-prompt">
            <Text variant="compact-title">What should it do each run?</Text>
          </label>
          <DictationTextarea
            id="agent-schedule-prompt"
            value={value.prompt}
            minRows={3}
            maxRows={8}
            autoSize
            placeholder="Check overnight compliance and summarize what is overdue."
            onValueChange={(prompt) => onChange({ ...value, prompt })}
            data-testid="agent-schedule-prompt"
          />
        </div>
      </>
    )}
  </div>
);

export default AgentScheduleFields;
